//================================================================
//  Component: Action Pane
//================================================================

//  Purpose: Sidepane to Approve/Reject/Reassign requests in bulk

//  Properties:
//      requestDoc = {useState, contains request data}
//      setRequestDoc = {useState, to reset after submission}
//      requestStatus = {useState, contains status ('Completed' or 'Rejected')}
//      setRequestList = {useState, to reset after submission}

//  Example:
//    <ActionPane
//      requestDoc={paneData}
//      setRequestDoc={setPaneData}
//      requestStatus={status}
//      setRequestList={setRequestList}
//    > </ActionPane>

//================================================================

//Libraries
import React, { useState, useEffect, useContext, useReducer } from 'react';

//Contexts
import { GetUser } from '../../Library/GlobalContexts';
import { SetToast } from '../../Library/GlobalContexts';

//Components
import UserSearchNative from '../UserSearchNative/UserSearchNative';

//Functions
import WriteDocument from '../../Library/WriteDocument';

//Images
import LoadingIcon from '../Images/Image_Loading_Ripple.svg';
import ClearIcon from '../Images/Icon_Clear_Teal.svg';

export default function ActionPane({
    requestDoc,
    setRequestDoc,
    requestStatus,
    setRequestList
}) {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);
    const setToast = useContext(SetToast);

    //------------------------------------------------------
    //  useState
    //------------------------------------------------------

    // Used to determine the status of the pane > 'pending', 'onload'
    const [paneStatus, setPaneStatus] = useState('pending');

    // Add comment to status change
    const [comment, setComment] = useState('');

    //------------------------------------------------------
    //  useReducer
    //------------------------------------------------------

    // Used to store the search data
    const [searchData, setSearchData] = useReducer(
        (state, newState) => ({ ...state, ...newState }),
        {
            'newUserInput': '', // email of the selected user
            'newUserName': '', // fullname of the selected user
            'newGivenname': '', // givenname of the selected user
            'newSurname': '', // surname of the selected user
            'newJobTitle': '', // jobtitle of the selected user
            'searchResults': '',
            'queryMessage': '',
        }
    );

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    
    // Disables the "Proceed" button if conditions aren't met
    function isSubmitDisabled() {

        // Enable button when dropdown option is selected
        if (requestStatus === 'Completed') return false;

        if (requestStatus === 'Reassign') 
            return searchData.newUserInput.length > 0 && comment.length > 0 ? false : true;

        if (requestStatus === 'Rejected') {
            return comment.length > 0 ? false : true;
        }

        return true;

    }


    function handleSubmit() {

        setPaneStatus('pending');

        const docWritePromises = [];

        // For task status changes
        if (['Completed', 'Rejected'].includes(requestStatus)){

            let activityAction;

            // For activity feed log
            if (requestStatus === 'Completed') activityAction = 'approved';
            else activityAction = 'rejected';

            requestDoc.forEach((request) => {

                const newActivityFeed = [...request.activityFeed, {
                    'action': activityAction,
                    'actionedby': {
                        'email': getUser.emailaddress,
                        'givenname': getUser.givenname,
                        'surname': getUser.surname,
                    },
                    'activitydate': new Date(),
                    'comments': comment
                }];

                const docObject = {
                    'status': requestStatus,
                    'activityfeed': newActivityFeed,
                    'lastmodifiedby': {
                        'email': getUser.emailaddress,
                        'givenname': getUser.givenname,
                        'surname': getUser.surname,
                    },
                    'lastmodifieddate': new Date(),
                    'comment': comment,
                    'rejectionreason': requestStatus === 'Rejected' ? comment : '',
                };

                docWritePromises.push(WriteDocument('requests', request.taskId, docObject, true))
            })
        }

        // For reassignments
        else {

            requestDoc.forEach((request) => {

                const newActivityFeed = [...request.activityFeed, {
                    'action': 'unassigned',
                    'actionedby': {
                        'email': getUser.emailaddress,
                        'givenname': getUser.givenname,
                        'surname': getUser.surname,
                    },
                    'activitydate': new Date(),
                    'comments': comment
                }, {
                    'action': 'assigned',
                    'actionedby': {
                        'email': searchData.newUserInput,
                        'givenname': searchData.newGivenname,
                        'surname': searchData.newSurname, 
                    },
                    'activitydate': new Date(),
                    'comments': comment
                }];

                const docObject = {
                    'activityfeed': newActivityFeed,
                    'assignedto': {
                        'email': searchData.newUserInput,
                        'givenname': searchData.newGivenname,
                        'surname': searchData.newSurname, 
                    },
                    'lastmodifiedby': {
                        'email': getUser.emailaddress,
                        'givenname': getUser.givenname,
                        'surname': getUser.surname,
                    },
                    'lastmodifieddate': new Date(),
                    'comment': comment,
                };

                docWritePromises.push(WriteDocument('requests', request.taskId, docObject, true))

            });
        };

        // Settle promises
        Promise.all(docWritePromises)
            .then(() => {

                // Reset and close side pane
                setRequestDoc([]);
                setRequestList([]);
                setComment('');

                // Show successful toast
                setToast({
                    'type': 'success',
                    'message': 'Successfully updated the request(s).'
                })

            }).catch((error) => {
                console.log('error', error);

                // Show error toast
                setToast({
                    'type': 'error',
                    'message': 'Failed to save changes.'
                })
            });

    };

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Onload
    useEffect(() => {

        // Close the pane if there are issues
        if (requestDoc.length === 0) return;
        if (paneStatus === 'onload') return;

        setPaneStatus('onload');

        // eslint-disable-next-line
    }, [requestDoc]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    if (requestDoc.length === 0) return null;

    //====================================================
    //  Pending
    //====================================================

    if (paneStatus === 'pending') {
        return (
            <div className='Pane-Background' >
                <dialog className='Pane-Container flex flex-col gap-2 pb-0 items-center'>

                    <img className='w-[200px] self-center' alt='loading-circle-icon' src={LoadingIcon}></img>

                    <button className='Primary-Button w-fit self-center' onClick={() => {

                        setRequestDoc([]);
                        setPaneStatus('pending');

                    }}>
                        Cancel
                    </button>

                </dialog >
            </div>
        )
    }

    //====================================================
    //  Onload
    //====================================================

    return (
        <div className='Pane-Background'>
            <dialog className={`${requestStatus === 'Rejected' ? 'Pane-Container-Red' : 'Pane-Container'}`}>
                <div className='grid grid-rows-[70px_1fr_70px] items-center justify-between w-full overflow-hidden'>

                    {/* ------------------------------------------------------ */}
                    {/*  Header                                                */}
                    {/* ------------------------------------------------------ */}

                    <div className='flex flex-col gap-2 p-4'>
                        <h4 className='text-[22px] p-0 m-0'> Approval Status </h4>
                    </div>

                    {/* ------------------------------------------------------ */}
                    {/*  Body Container                                        */}
                    {/* ------------------------------------------------------ */}

                    <div className='w-[50vw] h-full flex flex-col overflow-y-auto p-4 px-4 py-2 border-solid border gap-3'>

                        <div className='flex flex-wrap gap-2 py-2'>
                            {
                                requestDoc.map((request, index) => (
                                    <div key={index} className='flex flex-row gap-2 py-1 px-3 rounded-lg bg-[#DDF3F5] text-[#1C96A3]'>
                                        {`${request?.parentrequestid?.toUpperCase()} ${request?.taskDescription}`}
                                        <img src={ClearIcon} alt='Delete' className='w-[20px] cursor-pointer' onClick={() => {

                                            setRequestDoc(requestDoc.filter(item => item.taskId !== request.taskId))

                                        }}></img>
                                    </div>
                                ))
                            }
                        </div>

                        <label className='font-semibold'> Change status:</label>

                        <input
                            className='Input-Field-Text'
                            value={
                                requestStatus === 'Completed' ?
                                    'Approve' : 
                                requestStatus === 'Rejected' ?
                                    'Reject' :
                                requestStatus}
                            disabled={true}
                        ></input>

                        <div className='flex flex-col gap-3' hidden={requestStatus !== 'Reassign'}>
                            <label className='font-semibold'> 
                                Reassign request to: <span className='text-[#C4314B]'>*</span>
                            </label>
                            <UserSearchNative 
                                selectedUser={searchData}
                                setSelectedUser={setSearchData}
                                errorMessage={undefined}
                            ></UserSearchNative>                                   
                        </div>

                        <div className='flex flex-col gap-4'>
                            <label className='font-semibold'> Comment: {requestStatus !== 'Completed' && <span className='text-[#C4314B]'>*</span>} </label>
                            <textarea
                                className='Input-Field-TextArea'
                                placeholder={`Add a comment${
                                    requestStatus === 'Rejected' ? 
                                        ' to reject this request' : 
                                    requestStatus === 'Reassign' ?
                                        'for the new approver...' :
                                        '...'}`}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            ></textarea>
                        </div>

                    </div>

                    {/* ------------------------------------------------------ */}
                    {/*  Buttons Container                                     */}
                    {/* ------------------------------------------------------ */}

                    <div className='flex flex-row my-3 gap-2 px-4 py-3' >

                        {/* Submit - Disabled if status is Reject and there is no comment. */}
                        <button
                            className={requestStatus === 'Rejected' ? 'Primary-Button-Red' : 'Primary-Button'}
                            disabled={isSubmitDisabled()}
                            type='submit'
                            value='submit'
                            onClick={() => { handleSubmit() }}>
                            {requestStatus === 'Completed' ?
                                'Approve' : 
                            requestStatus === 'Rejected' ?
                                'Reject' :
                            requestStatus}
                        </button>

                        {/* Cancel */}
                        <button
                            className={requestStatus === 'Rejected' ? 'Secondary-Button-Black' : 'Secondary-Button'}
                            onClick={() => {

                                setRequestDoc([]);
                                setComment('');
                                setPaneStatus('pending');
                                setSearchData({
                                    'newUserInput': '', // email of the selected user
                                    'newUserName': '', // fullname of the selected user
                                    'newGivenname': '', // givenname of the selected user
                                    'newSurname': '', // surname of the selected user
                                    'newJobTitle': '', // jobtitle of the selected user
                                    'searchResults': '',
                                    'queryMessage': '',
                                });

                            }}>
                            Cancel
                        </button>

                    </div>
                </div>
            </dialog>
        </div>
    )
}
